// Article.jsx - One guide. Markdown compiled at build time, so this renders as
// static HTML with no request and no loading state.
//
// The measure is deliberately narrow and the type is a touch larger than the rest
// of the site, because this is the only part anybody reads at length.

import { useEffect, useRef } from "react";
import { Link, useParams } from "react-router";

import { Seo, JsonLd, articleSchema, breadcrumbSchema, SITE } from "../lib/seo.jsx";
import { getArticle, related } from "../lib/content.js";
import * as analytics from "../lib/analytics.js";
import { day } from "../lib/format.js";
import { ArrowLeft, ArrowRight } from "../ui/icons.jsx";
import { Reveal, StaggerBoxed, ScrollProgress } from "../ui/motion.jsx";
import { Ornament } from "../ui/illustrations.jsx";
import { CtaBand, MobileCta } from "./parts.jsx";
import NotFound from "./NotFound.jsx";

export default function Article() {
  const { slug } = useParams();
  const article = getArticle(slug);
  const body = useRef(null);

  // Above the early return, because a hook cannot be called conditionally. It
  // looks the article up again rather than closing over one that may be absent.
  useEffect(() => {
    const found = getArticle(slug);
    if (found) analytics.trackViewGuide(slug, found.category);
  }, [slug]);

  if (!article) return <NotFound />;

  const more = related(slug);

  return (
    <>
      <Seo
        title={article.title}
        path={`/learn/${slug}`}
        description={article.description}
        type="article"
        image={SITE.ogLearn}
        publishedAt={article.date}
        updatedAt={article.updated}
      />
      <JsonLd data={articleSchema(article)} />
      <JsonLd data={breadcrumbSchema([
        { label: "Home", to: "/" },
        { label: "Learn", to: "/learn" },
        { label: article.title, to: `/learn/${slug}` },
      ])} />

      <ScrollProgress target={body} />

      <article ref={body} className="shell-narrow pt-8 md:pt-14 pb-14 md:pb-16">
        <Link to="/learn" className="inline-flex items-center gap-1.5 text-[13px] text-muted hover:text-ink transition-colors mb-8">
          <ArrowLeft size={15} /> All guides
        </Link>

        <p className="eyebrow mb-4">
          {article.category} &middot; {article.readMinutes} min read
          {article.date ? ` · ${day(article.date)}` : ""}
        </p>

        <h1 className="display display-xl text-ink">{article.title}</h1>
        {article.description && <p className="lede mt-6 text-muted">{article.description}</p>}

        <div className="flex items-center gap-4 my-8 md:my-10">
          <Ornament />
          <span className="flex-1 h-px bg-rule" />
        </div>

        {/* The markdown is our own, written into this repository, never user input. */}
        <div className="prose-igs" dangerouslySetInnerHTML={{ __html: article.html }} />

        <div className="mt-14 pt-8 border-t border-rule">
          <p className="hint leading-relaxed measure">
            Written by the people who do the weighing. If something here is wrong or unclear,
            tell us on <a href="tel:+1-401-315-4653" className="link text-ink font-semibold">(401) 315-4653</a> and
            we will fix it.
          </p>
        </div>
      </article>

      {more.length > 0 && (
        <section className="border-t border-rule bg-surface/40 band-tight">
          <div className="shell">
            <p className="eyebrow mb-6">Read next</p>
            <StaggerBoxed as="div" step={90} className="grid gap-7 md:gap-8 md:grid-cols-3">
              {more.map((entry) => (
                <Link key={entry.slug} to={`/learn/${entry.slug}`} className="group block h-full">
                  <p className="eyebrow mb-2">{entry.category}</p>
                  <h2 className="display display-sm text-ink group-hover:text-bronze-dark transition-colors">
                    {entry.title}
                  </h2>
                  <p className="hint mt-2 leading-relaxed line-clamp-2">{entry.description}</p>
                  <span className="inline-flex items-center gap-1.5 mt-3 text-[13px] font-semibold text-ink">
                    Read <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              ))}
            </StaggerBoxed>
          </div>
        </section>
      )}

      <CtaBand />
      <MobileCta />
    </>
  );
}
